/** `roulette` is the nap roulette: nobody knows when it goes off. */
export type Mode = 'focus' | 'short' | 'long' | 'roulette';
export type Status = 'idle' | 'running' | 'paused';

export interface Durations {
    focus: number; // minutes
    short: number;
    long: number;
    /** The nap roulette goes off at a random moment between these two (minutes). */
    rouletteMin: number;
    rouletteMax: number;
}

export const DEFAULT_DURATIONS: Durations = {
    focus: 25,
    short: 5,
    long: 15,
    rouletteMin: 1,
    rouletteMax: 20,
};
export const FOCUS_BEFORE_LONG_BREAK = 4;

export interface TimerState {
    mode: Mode;
    status: Status;
    remainingMs: number;
    /** Wall-clock end while running. The clock is derived from this so a throttled background tab cannot make the timer drift. */
    endsAt: number | null;
    /** Focus sessions finished since the last long break. */
    focusDone: number;
}

export const msFor = (mode: Mode, durations: Durations): number =>
    Math.max(1, mode === 'roulette' ? durations.rouletteMax : durations[mode]) *
    60_000;

/** The secret length of one roulette round: a random moment between the two limits, in whole seconds. */
export const pickRouletteMs = (
    durations: Durations,
    random: () => number = Math.random
): number => {
    const lo = Math.min(durations.rouletteMin, durations.rouletteMax);
    const hi = Math.max(durations.rouletteMin, durations.rouletteMax);
    const seconds = Math.round((lo + random() * (hi - lo)) * 60);
    return Math.max(5, seconds) * 1000;
};

export const initialTimer = (durations: Durations): TimerState => ({
    mode: 'focus',
    status: 'idle',
    remainingMs: msFor('focus', durations),
    endsAt: null,
    focusDone: 0,
});

export const start = (
    s: TimerState,
    now: number,
    durations?: Durations,
    random?: () => number
): TimerState => {
    if (s.status === 'running') return s;
    // A fresh roulette round draws its secret length now; a paused one keeps what is left.
    const remainingMs =
        s.mode === 'roulette' && s.status === 'idle' && durations
            ? pickRouletteMs(durations, random)
            : s.remainingMs;
    return { ...s, status: 'running', remainingMs, endsAt: now + remainingMs };
};

export const pause = (s: TimerState, now: number): TimerState =>
    s.status !== 'running'
        ? s
        : {
              ...s,
              status: 'paused',
              remainingMs: Math.max(0, (s.endsAt ?? now) - now),
              endsAt: null,
          };

export const tick = (
    s: TimerState,
    now: number
): { state: TimerState; finished: boolean } => {
    if (s.status !== 'running' || s.endsAt === null)
        return { state: s, finished: false };
    const remainingMs = Math.max(0, s.endsAt - now);
    return { state: { ...s, remainingMs }, finished: remainingMs === 0 };
};

/** Developer shortcut: jump to the last `seconds` of the current session (never lengthens it). */
export const skipToLast = (
    s: TimerState,
    now: number,
    seconds = 10
): TimerState => {
    const remainingMs = Math.min(s.remainingMs, seconds * 1000);
    return s.status === 'running'
        ? { ...s, remainingMs, endsAt: now + remainingMs }
        : { ...s, remainingMs };
};

export const selectMode = (
    s: TimerState,
    mode: Mode,
    durations: Durations
): TimerState => ({
    ...s,
    mode,
    status: 'idle',
    endsAt: null,
    remainingMs: msFor(mode, durations),
});

export const reset = (s: TimerState, durations: Durations): TimerState =>
    selectMode(s, s.mode, durations);

/** What comes after the current mode finishes (or is skipped): focus -> a break, any break -> focus. */
export const advance = (
    s: TimerState,
    durations: Durations,
    completedFocus: boolean
): TimerState => {
    if (s.mode === 'roulette') return selectMode(s, 'roulette', durations);
    if (s.mode === 'focus') {
        const focusDone = completedFocus ? s.focusDone + 1 : s.focusDone;
        const longBreak =
            completedFocus && focusDone >= FOCUS_BEFORE_LONG_BREAK;
        return {
            ...selectMode(s, longBreak ? 'long' : 'short', durations),
            focusDone: longBreak ? 0 : focusDone,
        };
    }
    return selectMode(s, 'focus', durations);
};

export interface UpcomingTimer {
    mode: Mode;
    ms: number;
    /** `focusDone` once this timer is the current one. */
    focusDone: number;
}

/**
 * The timers that follow the current one if each is started the moment the last ends (what the phone's alarm
 * button does without the app). The current timer is taken to finish normally, so a focus session counts.
 */
export const upcomingTimers = (
    s: Pick<TimerState, 'mode' | 'focusDone'>,
    durations: Durations,
    count: number,
    random?: () => number
): UpcomingTimer[] => {
    const out: UpcomingTimer[] = [];
    let cur: TimerState = {
        mode: s.mode,
        focusDone: s.focusDone,
        status: 'idle',
        remainingMs: 0,
        endsAt: null,
    };
    for (let i = 0; i < count; i++) {
        cur = advance(cur, durations, cur.mode === 'focus');
        out.push({
            mode: cur.mode,
            ms:
                cur.mode === 'roulette'
                    ? pickRouletteMs(durations, random)
                    : msFor(cur.mode, durations),
            focusDone: cur.focusDone,
        });
    }
    return out;
};

const MODES: Mode[] = ['focus', 'short', 'long', 'roulette'];

/**
 * The timer the phone started on its own (the alarm's button, app away), as the page's state: running, with
 * the end time the phone is using. An unknown mode leaves the state as it is.
 */
export const joinNativeTimer = (
    s: TimerState,
    native: { endsAt: number; mode: string; focusDone: number },
    now: number
): TimerState =>
    MODES.includes(native.mode as Mode)
        ? {
              ...s,
              mode: native.mode as Mode,
              focusDone: native.focusDone,
              status: 'running',
              endsAt: native.endsAt,
              remainingMs: Math.max(0, native.endsAt - now),
          }
        : s;

export const formatClock = (ms: number): string => {
    const total = Math.ceil(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};
